/**
 * FormFill — renders a form's fields as inputs and collects answers
 * in the exact shape backend db/forms.js validateAnswers expects
 * (2026-09-07, flag `forms`).
 *
 *   short_text / long_text  → string
 *   number                  → string in state, Number on submit
 *   date                    → 'YYYY-MM-DD'
 *   single_select / dropdown→ one option string
 *   multi_select            → array of option strings
 *   rating                  → integer 1..config.scale
 *   nps                     → integer 0..10
 *   loss_reason             → loss-reason id (options are {id,label})
 *
 * Shared by the public /form/<token> page and the Decision Room
 * "Forms" section on the prospect side, so both buyer surfaces render
 * a question identically. `tone` picks the palette: 'light' is the
 * hardcoded buyer palette every prospect surface pins to; 'room' uses
 * the --room-* tokens for the seller-side preview inside the app.
 */

import { useId } from 'react';

const TONES = {
  light: {
    card: '#fff', border: '#cbd5e1', borderSoft: '#e2e8f0', text: '#0f172a',
    body: '#334155', muted: '#64748b', subtle: '#94a3b8', surface: '#f8fafc',
  },
  room: {
    card: 'var(--room-card, #fff)', border: 'var(--room-border, #e2e8f0)',
    borderSoft: 'var(--room-border-soft, #f1f5f9)', text: 'var(--room-text-strong, #0f172a)',
    body: 'var(--room-text-body, #334155)', muted: 'var(--room-text-muted, #64748b)',
    subtle: 'var(--room-text-subtle, #94a3b8)', surface: 'var(--room-surface, #f8fafc)',
  },
};

/** First unmet requirement, as the message the backend would send; null when clean. */
export function validateRequired(fields, values) {
  for (const f of fields || []) {
    if (!f.required) continue;
    const v = values ? values[f.id] : undefined;
    const missing = v === undefined || v === null
      || (typeof v === 'string' && v.trim() === '')
      || (Array.isArray(v) && v.length === 0);
    if (missing) return `"${f.label}" is required`;
  }
  return null;
}

/** Strip untouched keys so the server never sees empty strings for optional fields. */
export function answersForSubmit(fields, values) {
  const out = {};
  for (const f of fields || []) {
    const v = values ? values[f.id] : undefined;
    if (v === undefined || v === null) continue;
    if (typeof v === 'string' && v.trim() === '') continue;
    if (Array.isArray(v) && v.length === 0) continue;
    out[f.id] = f.fieldType === 'number' ? Number(v) : v;
  }
  return out;
}

function optionKey(o) {
  return typeof o === 'string' ? o : (o && o.id) || '';
}
function optionLabel(o) {
  return typeof o === 'string' ? o : (o && o.label) || '';
}

export default function FormFill({ fields, values, onChange, disabled = false, accent = '#7c3aed', tone = 'light' }) {
  const t = TONES[tone] || TONES.light;
  const baseId = useId();
  const inputStyle = {
    width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 10,
    border: `1px solid ${t.border}`, background: t.card, color: t.text,
    fontSize: 15, fontFamily: 'inherit', outline: 'none',
  };
  const set = (id, v) => { if (!disabled) onChange(id, v); };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      {(fields || []).map((f, i) => {
        const v = values ? values[f.id] : undefined;
        const fid = `${baseId}-${f.id}`;
        const label = (
          <label htmlFor={fid} style={{ display: 'block', fontSize: 15, fontWeight: 600, color: t.body, marginBottom: 8 }}>
            <span style={{ color: t.subtle, fontWeight: 500, marginRight: 8 }}>{i + 1}.</span>
            {f.label}
            {f.required && <span aria-hidden="true" style={{ color: '#dc2626', marginLeft: 4 }}>*</span>}
          </label>
        );

        switch (f.fieldType) {
          case 'short_text':
            return (
              <div key={f.id}>{label}
                <input id={fid} type="text" value={v || ''} maxLength={500} disabled={disabled} style={inputStyle}
                  onChange={(e) => set(f.id, e.target.value)} />
              </div>
            );
          case 'long_text':
            return (
              <div key={f.id}>{label}
                <textarea id={fid} value={v || ''} maxLength={5000} rows={4} disabled={disabled} style={{ ...inputStyle, resize: 'vertical' }}
                  onChange={(e) => set(f.id, e.target.value)} />
              </div>
            );
          case 'number':
            return (
              <div key={f.id}>{label}
                <input id={fid} type="number" value={v ?? ''} disabled={disabled} style={{ ...inputStyle, maxWidth: 240 }}
                  onChange={(e) => set(f.id, e.target.value)} />
              </div>
            );
          case 'date':
            return (
              <div key={f.id}>{label}
                <input id={fid} type="date" value={v || ''} disabled={disabled} style={{ ...inputStyle, maxWidth: 240 }}
                  onChange={(e) => set(f.id, e.target.value)} />
              </div>
            );
          case 'dropdown':
          case 'loss_reason':
            return (
              <div key={f.id}>{label}
                <select id={fid} value={v || ''} disabled={disabled} style={{ ...inputStyle, maxWidth: 420 }}
                  onChange={(e) => set(f.id, e.target.value)}>
                  <option value="">{f.fieldType === 'loss_reason' ? 'Pick a reason…' : 'Choose…'}</option>
                  {(f.options || []).map((o) => (
                    <option key={optionKey(o)} value={optionKey(o)}>{optionLabel(o)}</option>
                  ))}
                </select>
              </div>
            );
          case 'single_select':
            return (
              <fieldset key={f.id} style={{ border: 0, padding: 0, margin: 0 }}>
                <legend style={{ padding: 0, fontSize: 15, fontWeight: 600, color: t.body, marginBottom: 8 }}>
                  <span style={{ color: t.subtle, fontWeight: 500, marginRight: 8 }}>{i + 1}.</span>{f.label}
                  {f.required && <span aria-hidden="true" style={{ color: '#dc2626', marginLeft: 4 }}>*</span>}
                </legend>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {(f.options || []).map((o) => {
                    const checked = v === o;
                    return (
                      <label key={o} style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10,
                        border: `1px solid ${checked ? accent : t.borderSoft}`, background: checked ? `${accent}10` : t.card,
                        color: t.text, cursor: disabled ? 'default' : 'pointer', fontSize: 15,
                      }}>
                        <input type="radio" name={fid} value={o} checked={checked} disabled={disabled}
                          onChange={() => set(f.id, o)} style={{ accentColor: accent }} />
                        {o}
                      </label>
                    );
                  })}
                </div>
              </fieldset>
            );
          case 'multi_select': {
            const arr = Array.isArray(v) ? v : [];
            return (
              <fieldset key={f.id} style={{ border: 0, padding: 0, margin: 0 }}>
                <legend style={{ padding: 0, fontSize: 15, fontWeight: 600, color: t.body, marginBottom: 8 }}>
                  <span style={{ color: t.subtle, fontWeight: 500, marginRight: 8 }}>{i + 1}.</span>{f.label}
                  {f.required && <span aria-hidden="true" style={{ color: '#dc2626', marginLeft: 4 }}>*</span>}
                  <span style={{ color: t.subtle, fontWeight: 400, fontSize: 13, marginLeft: 8 }}>Pick any</span>
                </legend>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {(f.options || []).map((o) => {
                    const checked = arr.includes(o);
                    return (
                      <label key={o} style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10,
                        border: `1px solid ${checked ? accent : t.borderSoft}`, background: checked ? `${accent}10` : t.card,
                        color: t.text, cursor: disabled ? 'default' : 'pointer', fontSize: 15,
                      }}>
                        <input type="checkbox" value={o} checked={checked} disabled={disabled} style={{ accentColor: accent }}
                          onChange={(e) => set(f.id, e.target.checked ? [...arr, o] : arr.filter((x) => x !== o))} />
                        {o}
                      </label>
                    );
                  })}
                </div>
              </fieldset>
            );
          }
          case 'rating': {
            const scale = Number(f.config && f.config.scale) || 5;
            return (
              <div key={f.id} role="group" aria-labelledby={`${fid}-lbl`}>
                <div id={`${fid}-lbl`} style={{ fontSize: 15, fontWeight: 600, color: t.body, marginBottom: 8 }}>
                  <span style={{ color: t.subtle, fontWeight: 500, marginRight: 8 }}>{i + 1}.</span>{f.label}
                  {f.required && <span aria-hidden="true" style={{ color: '#dc2626', marginLeft: 4 }}>*</span>}
                </div>
                <ScaleButtons min={1} max={scale} value={v} onPick={(n) => set(f.id, n)} accent={accent} tone={t} disabled={disabled} />
              </div>
            );
          }
          case 'nps':
            return (
              <div key={f.id} role="group" aria-labelledby={`${fid}-lbl`}>
                <div id={`${fid}-lbl`} style={{ fontSize: 15, fontWeight: 600, color: t.body, marginBottom: 8 }}>
                  <span style={{ color: t.subtle, fontWeight: 500, marginRight: 8 }}>{i + 1}.</span>{f.label}
                  {f.required && <span aria-hidden="true" style={{ color: '#dc2626', marginLeft: 4 }}>*</span>}
                </div>
                <ScaleButtons min={0} max={10} value={v} onPick={(n) => set(f.id, n)} accent={accent} tone={t} disabled={disabled} />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: t.subtle, marginTop: 6, maxWidth: 520 }}>
                  <span>Not at all likely</span><span>Extremely likely</span>
                </div>
              </div>
            );
          default:
            return null;
        }
      })}
    </div>
  );
}

function ScaleButtons({ min, max, value, onPick, accent, tone, disabled }) {
  const items = [];
  for (let n = min; n <= max; n += 1) items.push(n);
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxWidth: 520 }}>
      {items.map((n) => {
        const active = Number(value) === n;
        return (
          <button
            key={n}
            type="button"
            aria-pressed={active}
            disabled={disabled}
            onClick={() => onPick(n)}
            style={{
              minWidth: 40, height: 40, borderRadius: 10, fontFamily: 'inherit', fontSize: 15, fontWeight: 600,
              border: `1px solid ${active ? accent : tone.border}`,
              background: active ? accent : tone.card, color: active ? '#fff' : tone.body,
              cursor: disabled ? 'default' : 'pointer',
            }}
          >
            {n}
          </button>
        );
      })}
    </div>
  );
}
